import React from "react";
import AppIcon from "../../../components/ui/AppIcon";

/**
 * NotificationPermissionModal
 *
 * Mobile-only, explicit notification opt-in shown after login.
 * Presentational only — the parent owns the show/hide logic and the
 * actual `Notification.requestPermission()` call (triggered from "Allow",
 * which provides the user gesture mobile browsers require).
 *
 * Props:
 *   - onAllow:       () => void   user tapped "Allow"
 *   - onRemindLater: () => void   user tapped "Remind me later"
 *   - denied:        boolean      browser permission is already "denied"
 */
export default function NotificationPermissionModal({
  onAllow,
  onRemindLater,
  denied = false,
}) {
  return (
    <div
      className="md:hidden fixed inset-0 z-[80] flex items-end justify-center bg-black/40 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Enable notifications"
    >
      <div className="w-full max-w-md bg-app-surface rounded-t-3xl shadow-modal px-6 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] animate-slide-up">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <AppIcon name="BellIcon" size={30} className="text-primary" />
          </div>

          <h2 className="text-lg font-bold text-text-primary tracking-tight">
            Turn on notifications
          </h2>

          <p className="mt-2 text-[13px] leading-relaxed text-text-secondary">
            {denied
              ? "Notifications are currently blocked. To get alerted about new messages, enable them for this site in your browser settings, then refresh."
              : "Get alerted the moment you receive a new message, even when the app isn't open."}
          </p>

          <div className="mt-6 w-full flex flex-col gap-2.5">
            <button
              onClick={onAllow}
              className="w-full py-3 rounded-2xl bg-primary text-white text-sm font-bold shadow-premium hover:bg-primary-dark active:scale-[0.98] transition-all"
            >
              {denied ? "Try again" : "Allow notifications"}
            </button>
            <button
              onClick={onRemindLater}
              className="w-full py-3 rounded-2xl text-sm font-bold text-text-muted hover:text-text-primary hover:bg-black/5 active:scale-[0.98] transition-all"
            >
              Remind me later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
